import React, { useContext, useEffect, useMemo, useState } from "react";
import SegmentedControl from "~/components/SegmentedControl/SegmentedControl";
import AddressPicker from "~/components/AddressPicker/AddressPicker";
import {
  filterSites,
  sortByDistance,
  buildPickupView,
  storeCapabilityTags,
} from "~/helpers/siteSort";
import {
  distanceInMetersBetween,
  metersToLabel,
} from "~/helpers/coordsHelpers";
import { isStoreOpen, getStoreTime } from "~/helpers/siteHelpers";
import { mergeDeliverable, sortForDelivery, type DeliverableSite } from "~/helpers/deliverableSites";
import Api from "~/api/api";
import TaxContext from "~/contexts/TaxContext";
import ThemeContext from "~/contexts/ThemeContext";
import SearchIcon from "~/icons/SearchIcon";
import LocationIcon from "~/icons/LocationIcon";
import ChevronDown from "~/icons/ChevronDown";
import type { SiteListObject } from "~/entities/siteEntities";
import { OrderSource } from "~/entities/orderEntities";
import styles from "./StoreFinder.module.css";

type Fulfilment = "PICKUP" | "DELIVERY";

type Props = {
  orgId: string;
  sites: SiteListObject[];
  prevSiteId?: string;
  initialFulfilment?: Fulfilment;
  /**
   * Pre-fill the Delivery tab's address (and trigger the deliverable check
   * on open) when the order already has a delivery address. Spares the user
   * retyping it — Sal's "Mobi To-Go" pattern.
   */
  initialAddress?: { formattedAddress: string; latitude: number; longitude: number };
  onSelect: (args: {
    site: SiteListObject;
    fulfilment: Fulfilment;
    deliveryAddress?: { formattedAddress: string; latitude: number; longitude: number };
  }) => void;
  loadDeliverableSites?: (lat: number, lng: number) => Promise<DeliverableSite[]>;
  /**
   * The current experience (order source). When set, the Delivery toggle is
   * offered only if a site has an *available* delivery order type for THIS
   * source — so e.g. catering delivery isn't offered at a store that only does
   * catering pickup, even though the store also offers online delivery.
   */
  orderSource?: OrderSource;
  /**
   * ISO country codes the org operates in — restricts the delivery autocomplete.
   * TODO: source this from the organisation. Add a `country`/`countryCodes` field
   * to the OrgSites entity (app/entities/siteEntities.ts) and pass it from the
   * StoreFinder call sites (OrderContext.tsx, routes/sites.tsx). Until then it
   * falls back to DEFAULT_REGION_CODES below.
   */
  regionCodes?: string[];
};

/** Fallback when the org has no country set yet. TODO: remove once orgs carry a country. */
const DEFAULT_REGION_CODES = ["NZ"];

// A site offers delivery only when it has an active DELIVERY order type that is
// also currently available. In the org-sites list, `serviceSettings` is
// pre-filtered to order types with a configured availability window, so
// membership == available (an order type can be active yet "Not available
// currently"). When an order source is given, the delivery type must also
// belong to that experience — so online delivery doesn't make catering offer
// delivery at a pickup-only catering store.
const hasDelivery = (s: SiteListObject, orderSource?: OrderSource) => {
  const serviceSettings = s.serviceSettings ?? [];
  // Only filter by availability when the list actually carries serviceSettings;
  // if a backend omits them, fall back to active-order-type existence so a
  // genuinely available delivery is never hidden.
  const hasAvailabilityData = serviceSettings.length > 0;
  const availableCodes = new Set(serviceSettings.map((ss) => ss.orderTypeCode));
  return !!s.orderTypes?.some(
    (o) =>
      o.active &&
      o.fulfillmentType === "DELIVERY" &&
      (!hasAvailabilityData || availableCodes.has(o.code)) &&
      (!orderSource || o.source === orderSource),
  );
};

const StoreFinder: React.FC<Props> = ({
  orgId, sites, prevSiteId, initialFulfilment = "PICKUP", initialAddress, onSelect, loadDeliverableSites,
  orderSource,
  regionCodes = DEFAULT_REGION_CODES,
}) => {
  const { formatAmount } = useContext(TaxContext);
  // Brand the picker from the org's own theme tokens (so BurgerFuel renders its
  // purple, other orgs render their own brand — never hardcoded). The accent is
  // the org's PRIMARY brand button (BurgerFuel #58348c), not the store-card
  // button token (which this org sets to a neutral grey).
  const { template } = useContext(ThemeContext);
  const card = template.storesScreen.card;
  const accent = template.global.button.primaryButtonColour;
  const accentFg = template.global.button.primaryButtonFontColour;
  const brandVars = {
    "--sf-accent": accent,
    "--sf-accent-fg": accentFg,
    "--sf-stroke": card.stroke ? card.strokeColour : "rgba(0,0,0,0.1)",
    "--seg-active-bg": accent,
    "--seg-active-fg": accentFg,
  } as React.CSSProperties;
  // formatAmount relies on TaxContext (present on the menu, absent on /sites) — fall
  // back to a plain cents→dollars format so fees never render as raw cents ("1200").
  const money = (cents: number) => formatAmount(cents) || `$${(cents / 100).toFixed(2)}`;
  const [fulfilmentSel, setFulfilmentSel] = useState<Fulfilment>(initialFulfilment);
  // Seed from initialAddress so a known delivery address comes back pre-filled
  // (Sal's "Mobi To-Go" pattern). Empty string when there's nothing to seed.
  const [address, setAddress] = useState(initialAddress?.formattedAddress ?? "");
  const [coords, setCoords] = useState<{ lat: number; lng: number; formatted: string } | null>(
    initialAddress
      ? { lat: initialAddress.latitude, lng: initialAddress.longitude, formatted: initialAddress.formattedAddress }
      : null,
  );
  const [deliverable, setDeliverable] = useState<DeliverableSite[] | null>(null);
  const [loading, setLoading] = useState(false);
  // Pickup-only state.
  const [search, setSearch] = useState("");
  const [pickupCenter, setPickupCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [resorting, setResorting] = useState(false);
  // Surfaced when geolocation is denied / unavailable / blocked (e.g. insecure
  // origin) so the button never silently does nothing.
  const [locateError, setLocateError] = useState<string | null>(null);

  const deliveryOffered = useMemo(
    () => sites.some((s) => hasDelivery(s, orderSource)),
    [sites, orderSource],
  );
  // Clamp the effective fulfilment to what's actually offered: if delivery isn't
  // offered (e.g. catering pickup-only), an initial "DELIVERY" must not leave the
  // tablist with no selected tab and the delivery address UI showing. Falls back
  // to Pickup so the Pickup tab stays selected and the pickup list renders.
  const fulfilment: Fulfilment = deliveryOffered ? fulfilmentSel : "PICKUP";
  const options = deliveryOffered
    ? [{ value: "PICKUP", label: "Pickup" }, { value: "DELIVERY", label: "Delivery" }]
    : [{ value: "PICKUP", label: "Pickup" }];

  // Restrict the delivery address autocomplete to the stores' region (centroid +
  // a margin past the farthest store), otherwise Google Places returns global
  // results (e.g. "5a" → an address in Egypt).
  const deliveryBias = useMemo(() => {
    const withCoords = sites.filter(
      (s) => s.address.latitude != null && s.address.longitude != null,
    );
    if (!withCoords.length) return undefined;
    const lat =
      withCoords.reduce((a, s) => a + (s.address.latitude as number), 0) /
      withCoords.length;
    const lng =
      withCoords.reduce((a, s) => a + (s.address.longitude as number), 0) /
      withCoords.length;
    const maxDist = withCoords.reduce(
      (m, s) =>
        Math.max(
          m,
          distanceInMetersBetween(
            lat,
            lng,
            s.address.latitude as number,
            s.address.longitude as number,
          ),
        ),
      0,
    );
    return { center: { lat, lng }, radius: maxDist + 30000 };
  }, [sites]);

  const runDeliverable = async (lat: number, lng: number, formatted: string) => {
    setCoords({ lat, lng, formatted });
    setLoading(true);
    try {
      const fn = loadDeliverableSites ?? ((la: number, ln: number) => Api.Order.deliverableSites(orgId, la, ln));
      setDeliverable(await fn(lat, lng));
    } finally {
      setLoading(false);
    }
  };

  // If a delivery address is already on the order, run the deliverable check
  // once on mount so the store list comes up pre-populated — the customer
  // doesn't have to retype their address.
  useEffect(() => {
    if (initialAddress) {
      runDeliverable(
        initialAddress.latitude,
        initialAddress.longitude,
        initialAddress.formattedAddress,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Pickup branch ----
  const showSearch = sites.length > 1;
  const pickupList = useMemo(() => {
    const filtered = showSearch ? filterSites(sites, { search }) : sites;
    return pickupCenter ? sortByDistance(filtered, pickupCenter) : filtered;
  }, [sites, showSearch, search, pickupCenter]);

  // Pin the recent/current store at the top (when browsing) as a shortcut — it
  // ALSO stays in its region group below (the pin is a shortcut, not a move).
  // While narrowing (search / locate) the list is ordered by relevance/distance,
  // so we don't pin then.
  const isNarrowing = !!search.trim() || !!pickupCenter;
  const recentSite = !isNarrowing
    ? pickupList.find((s) => s.id === prevSiteId)
    : undefined;

  // Split the list into region headings (BurgerFuel/Sal's style); collapses to a
  // flat list while searching or sorting by location.
  const pickupView = useMemo(
    () =>
      buildPickupView(pickupList, {
        searching: !!search.trim(),
        located: !!pickupCenter,
      }),
    [pickupList, search, pickupCenter],
  );

  // Region groups start collapsed — scan the headings, expand the one you want.
  // (The recent store is already pinned at the top for quick access.) Explicit
  // user toggles override the default.
  const [regionOverride, setRegionOverride] = useState<Record<string, boolean>>(
    {},
  );
  const isRegionOpen = (region: string) => regionOverride[region] ?? false;
  const toggleRegion = (region: string, open: boolean) =>
    setRegionOverride((prev) => ({ ...prev, [region]: !open }));

  const renderPickupRow = (site: SiteListObject) => {
    const open = isStoreOpen(site);
    const distance =
      pickupCenter &&
      site.address.latitude != null &&
      site.address.longitude != null
        ? metersToLabel(
            distanceInMetersBetween(
              pickupCenter.lat,
              pickupCenter.lng,
              site.address.latitude,
              site.address.longitude,
            ),
          )
        : null;
    return (
      <li key={site.id} className={styles.row}>
        <button
          type="button"
          className={styles.rowButton}
          onClick={() => onSelect({ site, fulfilment: "PICKUP" })}
        >
          <span className={styles.rowText}>
            <span className={styles.rowNameLine}>
              <span className={styles.rowName}>{site.name}</span>
              {site.id === prevSiteId && (
                <span className={styles.rowBadge}>Recent</span>
              )}
            </span>
            {(() => {
              // address1 is the full formatted address (already includes the
              // city); just trim a trailing country. Fall back to city.
              const line = (site.address.address1?.trim() ||
                site.address.city?.trim() ||
                "").replace(/,\s*New Zealand$/i, "");
              return line ? <span className={styles.rowSub}>{line}</span> : null;
            })()}
            <span className={styles.rowHours}>{getStoreTime(site)}</span>
            {(() => {
              const tags = storeCapabilityTags(site);
              return tags.length ? (
                <span className={styles.rowTags}>
                  {tags.map((t) => (
                    <span key={t} className={styles.tag}>
                      {t}
                    </span>
                  ))}
                </span>
              ) : null;
            })()}
          </span>
          <span className={styles.rowRight}>
            <span
              className={`${styles.statusPill} ${
                open ? styles.statusOpen : styles.statusClosed
              }`}
              title={getStoreTime(site)}
            >
              {open ? "Open" : "Closed"}
            </span>
            {distance && <span className={styles.rowDistance}>{distance}</span>}
          </span>
        </button>
      </li>
    );
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setLocateError(
        "Location isn’t available on this device — search by store or suburb instead.",
      );
      return;
    }
    setLocateError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        // Cross-fade the re-sorted list with a subtle blur.
        setResorting(true);
        setPickupCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        window.setTimeout(() => setResorting(false), 220);
      },
      // Denial / unavailable / timeout / insecure-origin: tell the user and
      // point them at search, rather than silently doing nothing.
      (err) => {
        setLocating(false);
        setLocateError(
          err.code === err.PERMISSION_DENIED
            ? "We couldn’t access your location. Allow location access in your browser, or search by store or suburb."
            : "We couldn’t find your location — search by store or suburb instead.",
        );
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  // ---- Delivery branch ----
  const deliveryList = useMemo(() => {
    if (!deliverable) return [];
    return sortForDelivery(mergeDeliverable(sites, deliverable));
  }, [sites, deliverable]);
  const anyDeliverable = deliveryList.some((s) => s.delivery?.deliverable);

  return (
    <div className={styles.finder} style={brandVars}>
      <SegmentedControl
        options={options}
        value={fulfilment}
        onChange={(v) => {
          setFulfilmentSel(v as Fulfilment);
          // Switching to Delivery while we already have an address (e.g. prefilled
          // from the order, or geocoded earlier) → run the deliverable lookup so
          // the store list comes back instead of going blank.
          if (v === "DELIVERY" && coords && !deliverable) {
            runDeliverable(coords.lat, coords.lng, coords.formatted);
          }
        }}
      />

      {fulfilment === "PICKUP" && (
        <div className={styles.pickup}>
          {showSearch && (
            <div className={styles.pickupControls}>
              <div className={styles.searchField}>
                <span className={styles.searchIcon} aria-hidden="true">
                  <SearchIcon />
                </span>
                <input
                  type="search"
                  className={styles.searchInput}
                  placeholder="Search by store or suburb"
                  aria-label="Search stores"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    if (locateError) setLocateError(null);
                  }}
                />
              </div>
              <button
                type="button"
                className={styles.locationButton}
                onClick={useMyLocation}
                disabled={locating}
              >
                <LocationIcon />
                {locating ? "Locating…" : "Use my location"}
              </button>
              {locateError && (
                <p className={styles.locateError} role="status">
                  {locateError}
                </p>
              )}
            </div>
          )}

          {pickupList.length === 0 ? (
            <p className={styles.empty}>No stores match “{search.trim()}”</p>
          ) : pickupView.mode === "flat" ? (
            <ul
              className={`${styles.list}${resorting ? ` ${styles.listResorting}` : ""}`}
            >
              {pickupView.sites.map((s) => renderPickupRow(s))}
            </ul>
          ) : (
            <>
              {/* Recent store pinned at the top as a shortcut (its purple RECENT
                  pill marks it). It also still appears in its region group below. */}
              {recentSite && (
                <section className={styles.recentSection}>
                  <ul className={styles.list}>{renderPickupRow(recentSite)}</ul>
                </section>
              )}
              {/* Collapsible region accordions — scan regions first, then expand
                  the one you want. Easy to navigate a large chain. */}
              <div className={styles.regionGroups}>
                {pickupView.groups.map((group) => {
                  const open = isRegionOpen(group.region);
                  return (
                      <section key={group.region} className={styles.regionGroup}>
                        <h3 className={styles.regionHeadingWrap}>
                          <button
                            type="button"
                            className={styles.regionToggle}
                            aria-expanded={open}
                            onClick={() => toggleRegion(group.region, open)}
                          >
                            <ChevronDown
                              color="var(--sf-accent)"
                              className={`${styles.regionChevron}${
                                open ? ` ${styles.regionChevronOpen}` : ""
                              }`}
                            />
                            <span className={styles.regionName}>
                              {group.region}
                            </span>
                            <span className={styles.regionCount}>
                              {group.sites.length}
                            </span>
                          </button>
                        </h3>
                        <div
                          className={styles.regionPanel}
                          data-expanded={open}
                          // Take collapsed content out of tab order + AT. `inert`
                          // is a DOM property (not in React 18.2 prop types), so
                          // set it imperatively via the ref.
                          ref={(el) => {
                            if (el) el.inert = !open;
                          }}
                        >
                          <div className={styles.regionPanelInner}>
                            <ul className={styles.list}>
                              {group.sites.map((s) => renderPickupRow(s))}
                            </ul>
                          </div>
                        </div>
                      </section>
                    );
                  })}
                </div>
            </>
          )}
        </div>
      )}

      {fulfilment === "DELIVERY" && (
        <div className={styles.delivery}>
          <AddressPicker
            placeholder="Enter your delivery address"
            address={address}
            setAddress={setAddress}
            onPlaceSelect={(d) => runDeliverable(d.latitude, d.longitude, d.formattedAddress)}
            storeLocation={deliveryBias?.center}
            radiusMeters={deliveryBias?.radius}
            regionCodes={regionCodes}
          />
          {loadDeliverableSites && (
            <button
              data-testid="test-pick-address"
              style={{ display: "none" }}
              onClick={() => runDeliverable(-36.865, 174.756, "Test Address")}
            />
          )}
          {loading && (
            <div className={styles.loading}>
              <span className={styles.spinner} aria-hidden="true" />
              <span>Finding stores that deliver…</span>
            </div>
          )}
          {deliverable && !anyDeliverable && (
            <div className={styles.outOfRange}>
              No stores deliver to that address.
              <span className={styles.outOfRangeHint}>
                Try a different address, or switch to Pickup.
              </span>
            </div>
          )}
          {deliverable && anyDeliverable && (
            <ul className={styles.list}>
              {deliveryList.map((site) => {
                const d = site.delivery as DeliverableSite | undefined;
                const ok = d?.deliverable;
                return (
                  <li key={site.id} className={styles.row}>
                    <button
                      type="button"
                      className={styles.rowButton}
                      disabled={!ok}
                      onClick={() =>
                        coords &&
                        onSelect({
                          site,
                          fulfilment: "DELIVERY",
                          deliveryAddress: {
                            formattedAddress: coords.formatted,
                            latitude: coords.lat,
                            longitude: coords.lng,
                          },
                        })
                      }
                    >
                      <span className={styles.rowText}>
                        <span className={styles.rowName}>{site.name}</span>
                        {site.address.city?.trim() && (
                          <span className={styles.rowSub}>
                            {site.address.city.trim()}
                          </span>
                        )}
                      </span>
                      {ok && d ? (
                        <span className={styles.deliveryMeta}>
                          <span className={styles.deliveryMetaLine}>
                            <span>{money(d.fee)}</span>
                            <span className={styles.deliveryDot}>·</span>
                            <span>~{d.etaMinutes}m</span>
                          </span>
                          {d.minOrder > 0 && (
                            <span className={styles.deliveryMetaSub}>
                              min {money(d.minOrder)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className={styles.rowMeta}>Not available</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreFinder;
